<template>
  <div class="notice-detail-page">

    <!-- 鼠标光晕 -->
    <div class="cursor-glow" ref="cursorGlow"></div>

    <!-- =================== Hero =================== -->
    <div class="hero" ref="hero" :style="heroStyle">
      <div class="orb orb--1" ref="orb1"></div>
      <div class="orb orb--2" ref="orb2"></div>
      <div class="hero__overlay"></div>
      <div class="hero__body">
        <div class="hero__badge" ref="heroBadge">
          <span class="hero__dot"></span>公告详情
        </div>
        <div class="clip-wrap">
          <h1 class="hero__title clip-inner">{{ notice.title || '公告详情' }}</h1>
        </div>
        <div class="clip-wrap" style="margin-top:.5rem;">
          <p class="hero__sub clip-inner">ANNOUNCEMENT DETAIL · {{ categoryLabel }}</p>
        </div>
        <div class="hero__actions" ref="heroActions">
          <button class="btn-primary magnetic" @click="applyShow">立即申请试用</button>
          <button class="btn-ghost magnetic" @click="goBack">返回公告列表</button>
        </div>
      </div>
    </div>

    <!-- =================== 面包屑导航 =================== -->
    <div class="breadcrumb-bar" ref="breadcrumb">
      <div class="breadcrumb-bar__inner">
        <span class="breadcrumb-item" @click="goBack">公告信息</span>
        <i class="el-icon-arrow-right breadcrumb-sep"></i>
        <span class="breadcrumb-item breadcrumb-item--active">{{ categoryLabel }}详情</span>
      </div>
    </div>

    <!-- =================== 文章内容 =================== -->
    <div class="article-section" ref="articleSection">
      <div class="article-wrap">

        <!-- 文章头部 -->
        <header class="article-header" ref="articleHeader">
          <!-- 封面图（有图才显示） -->
          <div v-if="notice.img" class="article-cover" ref="articleCover">
            <img :src="notice.img" :alt="notice.title" />
            <div class="article-cover__mask"></div>
          </div>

          <div class="article-header__meta">
            <span class="article-tag">{{ categoryLabel }}</span>
            <span class="article-date">
              <i class="el-icon-date"></i>
              {{ formattedDate }}
            </span>
          </div>
          <h2 class="article-title">{{ notice.title }}</h2>
          <div class="article-title-bar"></div>
        </header>

        <!-- 结构化信息卡片（有 infoItems 才显示） -->
        <div class="info-cards" ref="infoCards" v-if="notice.infoItems && notice.infoItems.length">
          <div class="info-card" v-for="info in notice.infoItems" :key="info.label">
            <div class="info-card__icon"><i :class="info.icon"></i></div>
            <div class="info-card__body">
              <span class="info-card__label">{{ info.label }}</span>
              <p class="info-card__value">{{ info.value }}</p>
            </div>
          </div>
        </div>

        <!-- 正文内容区 -->
        <div class="article-body" ref="articleBody">
          <!-- 当 content 是 HTML 富文本时用 v-html；否则按段落渲染 -->
          <template v-if="isHtmlContent">
            <div class="article-richtext" v-html="notice.content"></div>
          </template>
          <template v-else>
            <p
              v-for="(para, i) in contentParagraphs"
              :key="i"
              class="article-para"
            >{{ para }}</p>
          </template>

          <!-- 内容为空时的占位 -->
          <div v-if="!notice.content" class="article-empty">
            <i class="el-icon-document"></i>
            <p>暂无详细内容</p>
          </div>
        </div>

        <!-- 签名区 -->
        <div class="article-sig" ref="articleSig">
          <div class="article-sig__inner">
            <div class="article-sig__logo">
              <span class="article-sig__dot"></span>
              厦门鸿宁兴科技有限公司
            </div>
            <p class="article-sig__date">{{ formattedDate }}</p>
          </div>
        </div>

        <!-- 底部操作 -->
        <div class="article-footer" ref="articleFooter">
          <div class="article-footer__divider"></div>
          <div class="article-footer__actions">
            <button class="btn-back" @click="goBack">
              <i class="el-icon-arrow-left"></i>
              返回公告列表
            </button>
          </div>
        </div>

      </div>
    </div>

    <ApplyDialog :show.sync="show"></ApplyDialog>
  </div>
</template>

<script>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import ApplyDialog from '../../components/applyDialog.vue'

gsap.registerPlugin(ScrollTrigger)

/** 公告类型映射 */
const CATEGORY_MAP = {
  '0': '招标公告',
  '1': '中标公告',
  '2': '采购公告',
  '3': '通知公告',
}

export default {
  components: { ApplyDialog },
  data() {
    return {
      show: false,
    }
  },

  computed: {
    /** 从 sessionStorage 读取公告完整数据 */
    notice() {
      try {
        const raw = sessionStorage.getItem('noticeDetail')
        if (raw) return JSON.parse(raw)
      } catch (e) {
        // 解析失败时降级为空对象
      }
      return { title: '', content: '', time: '', img: '', type: '0', infoItems: [] }
    },

    /** 公告类别名称 */
    categoryLabel() {
      return CATEGORY_MAP[this.notice.type] || '公告详情'
    },

    /** 格式化日期：2023-03-10 → 2023 年 03 月 10 日 */
    formattedDate() {
      if (!this.notice.time) return ''
      const parts = this.notice.time.split('-')
      if (parts.length !== 3) return this.notice.time
      return `${parts[0]} 年 ${parts[1]} 月 ${parts[2]} 日`
    },

    /** 判断 content 是否为 HTML（含标签则当富文本处理） */
    isHtmlContent() {
      return /<[a-z][\s\S]*>/i.test(this.notice.content)
    },

    /**
     * 将纯文本按换行符切分为段落数组，过滤空行
     * @returns {string[]}
     */
    contentParagraphs() {
      if (!this.notice.content) return []
      return this.notice.content
        .split(/\n+/)
        .map(p => p.trim())
        .filter(p => p.length > 0)
    },

    /** Hero 背景：有封面图则用封面图，否则用默认图 */
    heroStyle() {
      const bg = this.notice.img
        ? `url(${this.notice.img})`
        : `url(${require('../../assets/cooperation6.png')})`
      return { backgroundImage: bg }
    },
  },

  mounted() {
    this.$nextTick(() => {
      this._initCursorGlow()
      this._initHeroAnimations()
      this._initArticle()
      this._initMagnetic()
    })
  },

  beforeDestroy() {
    ScrollTrigger.getAll().forEach(t => t.kill())
    document.removeEventListener('mousemove', this._cursorHandler)
  },

  methods: {
    /* ─── 光晕 ─── */
    _initCursorGlow() {
      const glow = this.$refs.cursorGlow
      if (!glow) return
      this._cursorHandler = (e) => {
        gsap.to(glow, { x: e.clientX, y: e.clientY, duration: 0.6, ease: 'power2.out' })
      }
      document.addEventListener('mousemove', this._cursorHandler)
    },

    /* ─── Hero 入场 ─── */
    _initHeroAnimations() {
      if (this.$refs.orb1) {
        gsap.to(this.$refs.orb1, { y: -50, x: 25, duration: 5, repeat: -1, yoyo: true, ease: 'sine.inOut' })
      }
      if (this.$refs.orb2) {
        gsap.to(this.$refs.orb2, { y: 40, x: -30, duration: 6.5, repeat: -1, yoyo: true, ease: 'sine.inOut', delay: 1 })
      }

      const tl = gsap.timeline({ delay: 0.2 })
      tl.from(this.$refs.heroBadge, { scale: 0, opacity: 0, duration: 0.5, ease: 'back.out(2)' })
        .from('.clip-inner', { yPercent: 105, opacity: 0, duration: 0.9, ease: 'expo.out', stagger: 0.18 }, '-=0.2')
        .from(Array.from(this.$refs.heroActions?.children || []), { y: 24, opacity: 0, duration: 0.5, ease: 'power3.out', stagger: 0.1 }, '-=0.4')

      gsap.to(this.$refs.orb1, {
        y: -100, ease: 'none',
        scrollTrigger: { trigger: this.$refs.hero, scrub: 1, start: 'top top', end: 'bottom top' },
      })
    },

    /* ─── 文章内容序列入场 ─── */
    _initArticle() {
      /* 面包屑淡入 */
      gsap.fromTo(this.$refs.breadcrumb,
        { y: -10, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.5, ease: 'power3.out',
          scrollTrigger: { trigger: this.$refs.breadcrumb, start: 'top 90%', once: true },
          clearProps: 'transform,opacity',
        }
      )

      /* 文章头部：封面图 + meta + 标题 scrub 揭示 */
      const header = this.$refs.articleHeader
      if (header) {
        const tl = gsap.timeline({
          scrollTrigger: { trigger: header, start: 'top 80%', end: 'top 35%', scrub: 0.6 },
        })
        const cover = header.querySelector('.article-cover')
        if (cover) {
          tl.fromTo(cover, { scale: 1.06, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.5 }, 0)
        }
        tl.fromTo(header.querySelector('.article-header__meta'),
          { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.4 }, 0.1)
        tl.fromTo(header.querySelector('.article-title'),
          { yPercent: 60, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.5 }, 0.25)
        tl.fromTo(header.querySelector('.article-title-bar'),
          { scaleX: 0 }, { scaleX: 1, duration: 0.4 }, 0.55)
      }

      /* 信息卡片：scrub 错落弹入 */
      const infoCards = this.$el.querySelectorAll('.info-card')
      if (infoCards.length && this.$refs.infoCards) {
        const tl = gsap.timeline({
          scrollTrigger: { trigger: this.$refs.infoCards, start: 'top 85%', end: 'top 40%', scrub: 0.6 },
        })
        infoCards.forEach((card, i) => {
          tl.fromTo(card, { x: -40, opacity: 0 }, { x: 0, opacity: 1, duration: 0.45 }, i * 0.1)
        })
      }

      /* 正文 + 签名 + 底部：逐一淡入 */
      ;[this.$refs.articleBody, this.$refs.articleSig, this.$refs.articleFooter].forEach((el, i) => {
        if (!el) return
        gsap.fromTo(el,
          { y: 30, opacity: 0 },
          {
            y: 0, opacity: 1, duration: 0.7, ease: 'power3.out',
            delay: i * 0.08,
            scrollTrigger: { trigger: el, start: 'top 88%', once: true },
            clearProps: 'transform,opacity',
          }
        )
      })
    },

    /* ─── 磁性按钮 ─── */
    _initMagnetic() {
      this.$el.querySelectorAll('.magnetic').forEach((btn) => {
        btn.addEventListener('mousemove', (e) => {
          const r = btn.getBoundingClientRect()
          gsap.to(btn, {
            x: (e.clientX - r.left - r.width  / 2) * 0.35,
            y: (e.clientY - r.top  - r.height / 2) * 0.35,
            duration: 0.3, ease: 'power2.out',
          })
        })
        btn.addEventListener('mouseleave', () => {
          gsap.to(btn, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.5)' })
        })
      })
    },

    applyShow() { this.show = true },
    goBack()    { this.$router.push({ path: '/notices' }) },
  },
}
</script>

<style lang="scss" scoped>
$blue:   #0E6EB8;
$blue-l: #38A0F5;
$dark:   #0D1B2E;
$tint:   #F0F7FF;
$muted:  #6B7280;
$r-xl:   1.25rem;
$tr:     all .3s cubic-bezier(.4,0,.2,1);

/* ─── 光晕 ─── */
.cursor-glow {
  position: fixed; width: 28rem; height: 28rem; border-radius: 50%;
  background: radial-gradient(circle, rgba(14,110,184,.1), transparent 70%);
  pointer-events: none; transform: translate(-50%,-50%); z-index: 9990;
  mix-blend-mode: screen; will-change: transform;
}

/* ─── Hero ─── */
.hero {
  height: 44rem;
  background: no-repeat center center / cover;
  position: relative; display: flex; align-items: center; overflow: hidden;

  .orb {
    position: absolute; border-radius: 50%; filter: blur(90px);
    pointer-events: none; z-index: 1; will-change: transform;
  }
  .orb--1 { width: 38rem; height: 38rem; background: rgba(14,110,184,.28); top: -10rem; right: -4rem; }
  .orb--2 { width: 24rem; height: 24rem; background: rgba(56,160,245,.2); bottom: 2rem; left: 38%; }

  &__overlay {
    position: absolute; inset: 0; z-index: 1;
    background: linear-gradient(110deg, rgba(8,28,60,.82) 0%, rgba(14,110,184,.38) 55%, rgba(0,0,0,.06) 100%);
  }
  &__body  { position: relative; z-index: 3; padding: 0 7.5rem; max-width: 65%; }
  &__badge {
    display: inline-flex; align-items: center; gap: .5rem;
    background: rgba(255,255,255,.12); backdrop-filter: blur(12px);
    border: 1px solid rgba(255,255,255,.25); color: #fff;
    font-size: .78rem; font-weight: 600; letter-spacing: .08em;
    padding: .38rem .95rem; border-radius: 5rem; margin-bottom: 1.2rem;
  }
  &__dot { width: .45rem; height: .45rem; border-radius: 50%; background: $blue-l; animation: pulse 2s infinite; }
  &__title {
    font-size: 2.5rem; font-weight: 800; color: #fff;
    line-height: 1.4; margin: 0 0 .6rem; text-shadow: 0 2px 20px rgba(0,0,0,.35);
    /* 标题过长时截断为两行 */
    display: -webkit-box; -webkit-line-clamp: 2; line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  &__sub   { font-size: .78rem; color: rgba(255,255,255,.7); letter-spacing: .06em; margin: 0 0 2rem; }
  &__actions { display: flex; gap: 1rem; flex-wrap: wrap; }
}

.clip-wrap { overflow: hidden; line-height: 1; }
.clip-inner { display: block; }

/* ─── 按钮 ─── */
.btn-primary {
  padding: .9rem 2.4rem; background: linear-gradient(135deg, $blue, $blue-l);
  color: #fff; font-size: .95rem; font-weight: 700; border-radius: 3rem;
  border: none; cursor: pointer; box-shadow: 0 6px 20px rgba(14,110,184,.4);
  transition: $tr; will-change: transform;
  &:hover { box-shadow: 0 10px 30px rgba(14,110,184,.55); }
}
.btn-ghost {
  padding: .9rem 2.4rem; background: rgba(255,255,255,.12); color: #fff;
  font-size: .95rem; font-weight: 700; border-radius: 3rem;
  border: 1.5px solid rgba(255,255,255,.45); cursor: pointer;
  transition: $tr; backdrop-filter: blur(8px); will-change: transform;
  &:hover { background: rgba(255,255,255,.22); border-color: #fff; }
}

/* ─── 面包屑 ─── */
.breadcrumb-bar {
  background: #fff;
  border-bottom: 1px solid rgba(14,110,184,.1);
  padding: .9rem 0;

  &__inner {
    max-width: 860px; margin: 0 auto; padding: 0 2rem;
    display: flex; align-items: center; gap: .5rem;
  }
}
.breadcrumb-item {
  font-size: .85rem; color: $muted; cursor: pointer; transition: color .25s;
  &:hover { color: $blue; }
  &--active { color: $blue; font-weight: 600; cursor: default; }
}
.breadcrumb-sep { font-size: .7rem; color: #ccc; }

/* ─── 文章区域 ─── */
.article-section {
  background: linear-gradient(180deg, $tint 0%, #fff 60%);
  padding: 4rem 2rem 6rem;
}
.article-wrap {
  max-width: 860px; margin: 0 auto;
  background: #fff;
  border: 1px solid rgba(14,110,184,.1);
  border-radius: 1.5rem;
  box-shadow: 0 8px 40px rgba(14,110,184,.1);
  overflow: hidden;
}

/* ─── 封面图 ─── */
.article-cover {
  width: 100%; height: 22rem; overflow: hidden; position: relative;

  img {
    width: 100%; height: 100%; object-fit: cover;
    transition: transform .6s ease;
  }
  &__mask {
    position: absolute; inset: 0;
    background: linear-gradient(180deg, transparent 50%, rgba(8,28,60,.35) 100%);
  }
  &:hover img { transform: scale(1.03); }
}

/* ─── 文章头部 ─── */
.article-header {
  border-bottom: 1px solid rgba(14,110,184,.08);

  &__meta {
    display: flex; align-items: center; gap: 1rem;
    padding: 2rem 3.5rem 0;
    margin-bottom: 1.25rem;
  }
}
.article-tag {
  font-size: .72rem; font-weight: 700; letter-spacing: .1em;
  color: $blue; background: rgba(14,110,184,.1);
  border: 1px solid rgba(14,110,184,.18);
  padding: .28rem .85rem; border-radius: 5rem;
}
.article-date {
  font-size: .82rem; color: $muted;
  display: flex; align-items: center; gap: .4rem;
  i { color: $blue-l; }
}
.article-title {
  font-size: 1.8rem; font-weight: 900; color: #1A2535;
  line-height: 1.4; margin: 0 0 1.25rem;
  padding: 0 3.5rem;
}
.article-title-bar {
  width: 3.5rem; height: 4px;
  background: linear-gradient(90deg, $blue, $blue-l);
  border-radius: 3px; margin: 0 3.5rem 2rem;
  transform-origin: left;
}

/* ─── 信息卡片 ─── */
.info-cards {
  padding: 2rem 3.5rem;
  display: flex; flex-direction: column; gap: .9rem;
  border-bottom: 1px solid rgba(14,110,184,.08);
  background: #fafcff;
}
.info-card {
  display: flex; align-items: flex-start; gap: 1.25rem;
  padding: 1rem 1.4rem;
  background: #fff; border: 1px solid rgba(14,110,184,.12);
  border-left: 4px solid $blue;
  border-radius: .875rem;
  transition: $tr;

  &:hover {
    border-left-color: $blue-l;
    box-shadow: 0 6px 24px rgba(14,110,184,.1);
    transform: translateX(5px);
  }
  &__icon {
    flex-shrink: 0;
    width: 2.4rem; height: 2.4rem;
    background: linear-gradient(135deg, rgba(14,110,184,.12), rgba(56,160,245,.08));
    border-radius: .625rem;
    display: flex; align-items: center; justify-content: center;
    i { font-size: 1.05rem; color: $blue; }
  }
  &__body { flex: 1; }
  &__label {
    display: block; font-size: .7rem; font-weight: 700; letter-spacing: .08em;
    color: $muted; text-transform: uppercase; margin-bottom: .25rem;
  }
  &__value { font-size: .88rem; font-weight: 600; color: #1A2535; margin: 0; line-height: 1.6; }
}

/* ─── 正文 ─── */
.article-body {
  padding: 2.5rem 3.5rem;
  border-bottom: 1px solid rgba(14,110,184,.06);
}
.article-para {
  font-size: .95rem; color: #1A2535; line-height: 1.9; margin: 0 0 1rem;
  text-indent: 2em;

  strong { color: $blue; font-weight: 800; }

  &:last-child { margin-bottom: 0; }
}

/* 富文本内容区：保留原有 HTML 样式 */
.article-richtext {
  font-size: .95rem; color: #1A2535; line-height: 1.9;

  p { margin: 0 0 1rem; text-indent: 2em; }
  img { max-width: 100%; border-radius: .75rem; margin: 1rem 0; }
  h1,h2,h3,h4 { color: $dark; margin: 1.5rem 0 .75rem; }
  strong,b { color: $blue; font-weight: 800; }
  a { color: $blue-l; text-decoration: underline; }
  ul,ol { padding-left: 1.5rem; margin: 0 0 1rem; }
  li { margin-bottom: .4rem; }
}

/* 内容为空时占位 */
.article-empty {
  display: flex; flex-direction: column; align-items: center;
  gap: .75rem; padding: 3rem 0; color: $muted;

  i { font-size: 2.5rem; opacity: .4; }
  p { font-size: .9rem; margin: 0; }
}

/* ─── 签名区 ─── */
.article-sig {
  padding: 0 3.5rem 2.5rem;

  &__inner {
    display: flex; flex-direction: column; align-items: flex-end; gap: .4rem;
    padding: 1.5rem 2rem;
    background: $tint; border-radius: 1rem;
    border: 1px solid rgba(14,110,184,.1);
  }
  &__logo {
    display: flex; align-items: center; gap: .5rem;
    font-size: 1rem; font-weight: 800; color: #1A2535;
  }
  &__dot {
    width: .5rem; height: .5rem; border-radius: 50%;
    background: linear-gradient(135deg, $blue, $blue-l); flex-shrink: 0;
  }
  &__date { font-size: .88rem; color: $muted; margin: 0; }
}

/* ─── 底部操作 ─── */
.article-footer {
  padding: 0 3.5rem 2.5rem;

  &__divider {
    height: 1px; background: rgba(14,110,184,.08); margin-bottom: 2rem;
  }
  &__actions { display: flex; justify-content: center; }
}
.btn-back {
  display: inline-flex; align-items: center; gap: .5rem;
  padding: .75rem 2rem;
  background: transparent; color: $blue;
  font-size: .9rem; font-weight: 700;
  border: 1.5px solid rgba(14,110,184,.3);
  border-radius: 3rem; cursor: pointer;
  transition: $tr;

  &:hover {
    background: $blue; color: #fff;
    border-color: $blue;
    box-shadow: 0 6px 20px rgba(14,110,184,.3);
    transform: translateX(-3px);
  }

  i { transition: transform .3s ease; }
  &:hover i { transform: translateX(-4px); }
}

/* ─── Keyframes ─── */
@keyframes pulse {
  0%,100% { opacity: 1; transform: scale(1); }
  50%      { opacity: .5; transform: scale(1.4); }
}
</style>

<style>
@media screen and (max-width: 600px) {
  .cursor-glow { display: none; }
  .notice-detail-page .hero { margin-top: 70px; min-height: 28rem; height: auto !important; padding: 3rem 0; }
  .hero__body  { padding: 0 1.5rem !important; max-width: 100% !important; }
  .article-wrap { border-radius: 0 !important; border-left: none !important; border-right: none !important; }
  .notice-detail-page .article-header__meta,
  .notice-detail-page .article-title,
  .notice-detail-page .article-title-bar { padding-left: 1.5rem !important; padding-right: 1.5rem !important; margin-left: 0 !important; margin-right: 0 !important; }
  .notice-detail-page .article-body,
  .notice-detail-page .article-sig,
  .notice-detail-page .article-footer { padding-left: 1.5rem !important; padding-right: 1.5rem !important; }
  .notice-detail-page .article-title  { font-size: 1.3rem !important; }
  .notice-detail-page .article-cover  { height: 14rem !important; }
}
</style>
